import { useMemo, useCallback } from 'react';
import useDatabase from './useDatabase';
import useWasmPricing from './useWasmPricing';
import { calculateSandwichPriceJS, calculateComboPriceJS, bulkPriceCalculationJS } from '../utils/priceCalculation';

/**
 * Custom hook that provides price calculation functions with WebAssembly optimization
 * Falls back to JavaScript calculation if WebAssembly is not available
 */
const usePricing = () => {
  const { 
    getAllMeats, 
    getAllBreads, 
    getAllCheeses, 
    getAllToppings,
    calculateSandwichPrice: calculateSandwichPriceDB 
  } = useDatabase();
  
  const {
    calculateComboPrice: calculateComboPriceWasm,
    bulkPriceCalculation: bulkPriceCalculationWasm,
    isWasmAvailable
  } = useWasmPricing();

  // Get all menu data for JavaScript fallback
  const menuData = useMemo(() => {
    if (!getAllMeats || !getAllBreads || !getAllCheeses || !getAllToppings) {
      return null;
    }
    
    return {
      meats: getAllMeats(),
      breads: getAllBreads(),
      cheeses: getAllCheeses(),
      toppings: getAllToppings()
    };
  }, [getAllMeats, getAllBreads, getAllCheeses, getAllToppings]);

  // Function to calculate sandwich price with WebAssembly if available, JavaScript fallback otherwise
  const calculateSandwichPrice = useCallback((options) => {
    // The WebAssembly logic (wasmConfig, styleId, toppingIds, dressingIds) was previously here
    // but is not currently used as calculateSandwichPriceDB is called directly.
    // These variables have been removed to address ESLint warnings.
    
    // Always use DB calculation for price to ensure consistent pricing
    return calculateSandwichPriceDB(options);
  }, [calculateSandwichPriceDB]);

  // Function to calculate combo price with WebAssembly if available, JavaScript fallback otherwise
  const calculateComboPrice = (sandwichPrice, drinkId, sideId) => {
    if (isWasmAvailable && calculateComboPriceWasm) {
      return calculateComboPriceWasm(sandwichPrice, drinkId, sideId);
    } else {
      return calculateComboPriceJS(sandwichPrice, drinkId, sideId);
    }
  };

  // Function to calculate multiple prices at once with WebAssembly if available
  const bulkPriceCalculation = (configs) => {
    if (isWasmAvailable && bulkPriceCalculationWasm) {
      return bulkPriceCalculationWasm(configs);
    } else if (menuData) {
      return bulkPriceCalculationJS(configs, menuData);
    }
    
    // If all else fails, calculate prices individually
    return configs.map(config => {
      const jsPrice = calculateSandwichPriceJS(config, menuData);
      return jsPrice;
    });
  };

  return {
    calculateSandwichPrice,
    calculateComboPrice,
    bulkPriceCalculation,
    isOptimized: isWasmAvailable
  };
};

export default usePricing;